import { ref } from 'vue'
import { captureException } from '@sentry/nuxt'
import { useAuth } from '~/composables/useAuth'
import { useToastStore } from '~/stores/toastStore'
import { ToastType } from '~/constants/toastTypes'
import { clearCookieAndRedirectToLogin } from '~/utils/apiUtils'

/** Consume page-load failures at the lifecycle boundary, not in API methods. */
export function useAuthenticatedLoad() {
  const { isAuthenticated } = useAuth()
  const toast = useToastStore()
  const isLoading = ref(false)

  async function load(
    steps: Array<() => Promise<unknown>>,
    failureMessage: string,
  ) {
    isLoading.value = true
    try {
      for (const step of steps) {
        if (!isAuthenticated.value) return
        await step()
      }
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 401) {
        // Revoked/expired sessions are an expected sign-in transition. The
        // rejected promise is consumed here and later loads are not started.
        await clearCookieAndRedirectToLogin()
        return
      }
      captureException(error)
      toast.show(failureMessage, ToastType.Error)
    } finally {
      isLoading.value = false
    }
  }

  return { isLoading, load }
}
